import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Trash2 } from 'lucide-react';

import type { WikiTemplate } from '@tasks/contracts';

import {
  createTemplate,
  deleteTemplate,
  listTemplates,
  updateTemplate,
} from '../../api/wiki';
import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { PageSection } from '../../components/Page';
import { Textarea } from '../../components/Textarea';
import { useToast } from '../../components/Toast';
import { humanizeError } from '../../lib/errors';
import styles from './WikiSettingsPage.module.css';

export interface WikiTemplatesSectionProps {
  readonly projectKey: string;
}

/**
 * Шаблоны страниц (docs/14 §9a, UC-WIKI-06): системные доступны всем, правка
 * системного создаёт проектную копию. Проектные шаблоны можно удалять.
 */
export function WikiTemplatesSection({ projectKey }: WikiTemplatesSectionProps) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [editing, setEditing] = useState<WikiTemplate | null>(null);
  const [editName, setEditName] = useState('');
  const [editBody, setEditBody] = useState('');
  const [newOpen, setNewOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newBody, setNewBody] = useState('');

  const queryKey = ['projects', projectKey, 'wiki', 'templates'] as const;
  const templatesQuery = useQuery({
    queryKey,
    queryFn: ({ signal }) => listTemplates(projectKey, signal),
    enabled: projectKey.length > 0,
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey });
  };
  const reportError = (error: unknown): void => {
    const human = humanizeError(error);
    toast.error(human.message, { code: human.code });
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (editing === null) return;
      await updateTemplate(projectKey, editing.id, {
        ...(editName.trim() === editing.name ? {} : { name: editName.trim() }),
        body: editBody,
      });
    },
    onSuccess: () => {
      invalidate();
      setEditing(null);
      toast.success('Шаблон сохранён');
    },
    onError: reportError,
  });

  const createMutation = useMutation({
    mutationFn: () => createTemplate(projectKey, { name: newName.trim(), body: newBody }),
    onSuccess: () => {
      invalidate();
      setNewOpen(false);
      setNewName('');
      setNewBody('');
      toast.success('Шаблон создан');
    },
    onError: reportError,
  });

  const deleteMutation = useMutation({
    mutationFn: (template: WikiTemplate) => deleteTemplate(projectKey, template.id),
    onSuccess: () => {
      invalidate();
      toast.success('Шаблон удалён');
    },
    onError: reportError,
  });

  const templates = templatesQuery.data ?? [];

  return (
    <PageSection title="Шаблоны">
      <p className={styles['hint']}>
        Шаблон предзаполняет тело новой страницы. Системные шаблоны («Решение»,
        «Разбор инцидента», «Инструкция») доступны всем проектам; при их правке
        создаётся проектная копия, системный оригинал не меняется.
      </p>
      <ul className={styles['templates']}>
        {templates.map((template) => (
          <li key={template.id} className={styles['templateRow']}>
            <span>
              {template.name}{' '}
              {template.isSystem ? <Badge tone="neutral">Системный</Badge> : null}
            </span>
            <span className={styles['actions']}>
              <Button
                size="sm"
                variant="ghost"
                icon={<Pencil size={14} />}
                onClick={() => {
                  setEditing(template);
                  setEditName(template.name);
                  setEditBody(template.body);
                }}
              >
                Изменить
              </Button>
              {template.isSystem ? null : (
                <Button
                  size="sm"
                  variant="danger"
                  icon={<Trash2 size={14} />}
                  loading={deleteMutation.isPending}
                  onClick={() => deleteMutation.mutate(template)}
                >
                  Удалить
                </Button>
              )}
            </span>
          </li>
        ))}
      </ul>

      {newOpen ? (
        <div className={styles['templateEditor']}>
          <Input
            label="Название"
            value={newName}
            onChange={(event) => setNewName(event.target.value)}
          />
          <Textarea
            label="Тело шаблона (markdown)"
            value={newBody}
            rows={8}
            onChange={(event) => setNewBody(event.target.value)}
          />
          <span className={styles['actions']}>
            <Button
              variant="primary"
              loading={createMutation.isPending}
              disabled={newName.trim().length === 0}
              onClick={() => createMutation.mutate()}
            >
              Создать шаблон
            </Button>
            <Button variant="ghost" onClick={() => setNewOpen(false)}>
              Отмена
            </Button>
          </span>
        </div>
      ) : (
        <Button variant="secondary" icon={<Plus size={14} />} onClick={() => setNewOpen(true)}>
          Новый шаблон
        </Button>
      )}

      {editing !== null ? (
        <div className={styles['templateEditor']}>
          <Input
            label="Название"
            value={editName}
            onChange={(event) => setEditName(event.target.value)}
          />
          <Textarea
            label="Тело шаблона (markdown)"
            value={editBody}
            rows={10}
            onChange={(event) => setEditBody(event.target.value)}
          />
          {editing.isSystem ? (
            <p className={styles['hint']}>
              При сохранении будет создана проектная копия шаблона «{editing.name}».
            </p>
          ) : null}
          <span className={styles['actions']}>
            <Button
              variant="primary"
              loading={saveMutation.isPending}
              onClick={() => saveMutation.mutate()}
            >
              Сохранить
            </Button>
            <Button variant="ghost" onClick={() => setEditing(null)}>
              Отмена
            </Button>
          </span>
        </div>
      ) : null}
    </PageSection>
  );
}
